import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBrands } from "../../contexts/BrandContext";
import { toast } from "sonner";
import {
  AlertOctagon, Clock, Building2, MapPin, RefreshCw, LogIn, Loader2,
  UserX, Search, Inbox,
} from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ManualClockDialog from "../ManualClockDialog";

/**
 * Onglet "En retard" — liste des shifts du jour pour lesquels le pointage
 * d'entrée est attendu mais absent (> 5 min après le début planifié).
 *
 * Présentation calquée sur EquipmentRequestsTab.jsx (barre colorée latérale,
 * badges majuscules, liste paginée par divider, recherche dédiée).
 */
export default function PointageRetardTab({ brands = [] }) {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [busyShiftId, setBusyShiftId] = useState(null);
  const [clockDialog, setClockDialog] = useState({ open: false, target: null });

  const load = useCallback(async (notify = false) => {
    try {
      setRefreshing(true);
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const { data } = await api.get("/pointage/late-arrivals", { params });
      setItems(data || []);
      if (notify) {
        api.post("/pointage/late-arrivals/notify").catch(() => {});
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBrandId]);

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(true), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => (
      (r.collaborateur_name || "").toLowerCase().includes(q)
      || (r.brand_name || "").toLowerCase().includes(q)
      || (r.site_name || "").toLowerCase().includes(q)
    ));
  }, [items, search]);

  const openManualClockIn = (row) => {
    setClockDialog({
      open: true,
      target: {
        shift_id: row.shift_id,
        collaborateur_name: row.collaborateur_name,
        planned_start: row.start_time,
        planned_end: row.end_time,
      },
    });
  };

  const handleMarkAbsent = async (row) => {
    if (!window.confirm(
      `Déclarer ${row.collaborateur_name} absent pour ce shift ?\n\n` +
      `Le collaborateur apparaîtra dans la rubrique "Collaborateurs absents" et son intervention sera ajoutée aux "Interventions vacantes à combler". Action tracée dans l'historique de sécurité.`
    )) return;
    setBusyShiftId(row.shift_id);
    try {
      await api.post(`/pointage/late-arrivals/${row.shift_id}/mark-absent`);
      toast.success(`${row.collaborateur_name} déclaré absent`);
      await load(false);
      window.dispatchEvent(new CustomEvent("plania:absences-updated"));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible de déclarer l'absence";
      toast.error(msg);
    } finally {
      setBusyShiftId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="pointage-retard-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <AlertOctagon size={11} /> Surveillance temps réel
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Pointages en retard
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            Shifts du jour dont le pointage d'entrée est manquant · {items.length} en retard
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="pointage-retard-refresh"
        >
          <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3">
        <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
          <Search size={10} /> Recherche
        </Label>
        <Input
          placeholder="Collaborateur, département ou site…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="pointage-retard-search"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="pointage-retard-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              {items.length === 0
                ? "Aucun retard à signaler pour le moment."
                : "Aucun retard ne correspond à votre recherche."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => {
              const brand = brands.find((b) => b.id === r.brand_id);
              return (
                <div
                  key={r.shift_id}
                  className="p-4 flex items-start gap-3 flex-wrap"
                  data-testid={`pointage-retard-row-${r.shift_id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: brand?.color || "#DC2626" }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-red-50 border-red-200 text-red-800">
                        <AlertOctagon size={10} /> En retard
                      </span>
                      <Link
                        to={`/collaborateurs/${r.collaborateur_id}/dossier`}
                        className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                        data-testid={`pointage-retard-collab-${r.shift_id}`}
                      >
                        {r.collaborateur_name}
                      </Link>
                      <span
                        className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-red-50 border-red-200 text-red-800"
                        data-testid={`pointage-retard-delay-${r.shift_id}`}
                      >
                        +{r.delay_minutes} min
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                      <p className="inline-flex items-center gap-1.5">
                        <Clock size={11} className="text-zinc-400" />
                        Prévu à <b className="text-zinc-800">{r.start_time}</b>
                        {r.end_time && <span className="text-zinc-400">→ {r.end_time}</span>}
                      </p>
                      <p className="inline-flex items-center gap-1.5">
                        <Building2 size={11} className="text-zinc-400" /> {r.brand_name}
                      </p>
                      {r.site_name && (
                        <p className="inline-flex items-center gap-1.5">
                          <MapPin size={11} className="text-zinc-400" /> {r.site_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openManualClockIn(r)}
                      disabled={busyShiftId === r.shift_id}
                      className="border-zinc-200 text-zinc-800 hover:bg-zinc-50"
                      data-testid={`pointage-retard-manual-clock-in-${r.shift_id}`}
                    >
                      {busyShiftId === r.shift_id
                        ? <Loader2 size={13} className="mr-1.5 animate-spin" />
                        : <LogIn size={13} className="mr-1.5" />}
                      Pointer entrée
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkAbsent(r)}
                      disabled={busyShiftId === r.shift_id}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`pointage-retard-mark-absent-${r.shift_id}`}
                    >
                      <UserX size={13} className="mr-1.5" /> Absent
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ManualClockDialog
        open={clockDialog.open}
        onOpenChange={(v) => setClockDialog((s) => ({ ...s, open: v }))}
        mode="in"
        target={clockDialog.target}
        onSuccess={() => load(false)}
      />
    </div>
  );
}
